<template>
    <div>
        <div class="d-flex justify-space-between align-center">
            <span class="title">
                <v-icon>list</v-icon>
                {{ $t('common.timeline.componentName') }}
            </span>
            <v-spacer></v-spacer>
            <timeline-filter v-if="isEmployee" @filter-timeline="loadData"/>
            <v-dialog v-model="activity.dialog" persistent max-width="600px">
                <template v-slot:activator="{ on }">
                    <v-btn
                        v-if="isEmployee && setup.showAdd"
                        color="primary"
                        v-on="on"
                        :loading="loading"
                        :disabled="loading"
                        :dark="!loading"
                        class="ml-1"
                    >{{ $t('common.add') }}</v-btn>
                </template>
                <v-form
                    ref="formActivity"
                    v-model="activity.valid"
                    lazy-validation
                >
                    <v-card>
                        <v-card-title>
                            <span class="headline">{{ $t('common.timeline.newInteraction') }}</span>
                        </v-card-title>
                        <v-card-text>
                            <v-row v-if="previewFiles && previewFiles.length > 0" >
                                <v-col cols="4" v-for="f in previewFiles" :key="f.index">
                                    <file-info :file="f" :type="'interaction'" :clearFile="clearFile"/>
                                </v-col>
                            </v-row >
                            <v-row >
                                <v-col cols="5" >
                                    <input-select :setup.sync="category"></input-select>
                                </v-col>
                                 <v-col cols="6">
                                    <input-date-picker :setup.sync="activity.datePickers.correctDate"></input-date-picker>
                                </v-col>
                            </v-row>
                            <editor v-model="activity.item.annotation" :files="addFiles"/>
                        </v-card-text>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn color="error" text @click="clearActivity" >{{ $t('common.cancel') }}</v-btn>
                            <v-btn color="primary" :disabled="fileLoading" @click.native="addActivity">{{ $t('common.save') }}</v-btn>
                        </v-card-actions>
                    </v-card>
                </v-form>
            </v-dialog>
            <v-btn
                v-if="setup.showUpdate"
                :loading="loading"
                :disabled="loading"
                color="purple"
                :dark="!loading"
                @click.prevent="loadData(filters, null)"
                class="ml-1"
            >
               <v-icon>refresh</v-icon>
            </v-btn>
        </div>
        <v-progress-linear :indeterminate="true" v-show="loading" class="mt-2"></v-progress-linear>
        <v-card-text v-show="!loading || interaction.results.length > 0">
            <v-timeline align-top dense>
                <v-timeline-item
                    v-for="(item, index) in interaction.results"
                    :key="index"
                    :color="item.note.responsible ? item.note.responsible.profile.color : '#F2C511'"
                    small
                    fill-dot
                >
                    <template v-slot:icon>
                        <v-avatar size="30" :color="item.note.responsible ? item.note.responsible.user.name  : item.note.clientResponsible.name">
                            <span class="white--text avatar--letter">{{ item.note.responsible ? item.note.responsible.user.name  : item.note.clientResponsible.name | initialName }}</span>
                        </v-avatar>
                    </template>

                    <v-card class="elevation-2">
                        <v-card-title class="subtitle-2 pb-2 pt-2" v-if="setup.timeline.className === 'Employee'">{{ item.note.interaction }} - {{ item.note.client.name }}{{ (item.note.demand)? ' - ' + item.note.demand.demand : '' }} - {{ item.note.correctDate | momentFormatDate("dddd, D MMMM YYYY") }}</v-card-title>
                        <v-card-title class="subtitle-2 pb-2 pt-2" v-else-if="setup.timeline.className === 'Client'">{{ item.note.interaction }}{{ (item.note.demand) ? ' - ' + item.note.demand.demand : '' }} - {{ item.note.responsible ? item.note.responsible.user.name : item.note.clientResponsible.name }} - {{ item.note.correctDate | momentFormatDate("dddd, D MMMM YYYY") }}</v-card-title>
                        <v-card-title class="subtitle-2 pb-2 pt-2" v-else>{{ item.note.interaction }} - {{ item.note.responsible ? item.note.responsible.user.name : item.note.clientResponsible.name }} - {{ item.note.correctDate | momentFormatDate("dddd, D MMMM YYYY") }}</v-card-title>
                        <v-card-text class="pb-0">
                            <read-only class="body-2" :text="item.note.annotation" />
                            <div v-if="item.files && item.files.length > 0">
                                <v-col cols="3" v-for="f in item.files" :key="f.objectId">
                                    <file-info :file="f.file" :id="f.objectId" :type="'interaction'" :clearFile="clearFile"/>
                                </v-col>
                            </div>
                        </v-card-text>
                        <v-card-actions class="pb-0 pt-0">
                            <v-spacer></v-spacer>
                            <v-btn
                                v-if="setup.isEmployee"
                                color="error"
                                text
                                small
                                @click.prevent="deleteActivity(index, item)"
                            >{{ $t('common.remove') }}</v-btn>
                        </v-card-actions>
                    </v-card>
                </v-timeline-item>
            </v-timeline>
            <v-col cols="12" class="text-right">
                <v-btn
                    v-if="hasMoreInteractions"
                    :loading="loading"
                    :disabled="loading"
                    @click="loadData(filters, null)"
                    small
                    text
                >
                    {{ $t('common.loadMore') }}
                    <span slot="loader" class="custom-loader">
                        <v-icon light>cached</v-icon>
                    </span>
                </v-btn>
            </v-col>
        </v-card-text>
    </div>
</template>

<script>
import API from '@/api'
import ReadOnly from '@/components/ReadOnly'
import FileInfo from '@/components/FileInfo'
import Editor from '@/components/Editor'
import InputSelect from '@/components/InputSelect'
import InputDatePicker from '@/components/InputDatePicker'
import TimelineFilter from '@/components/TimelineFilter'

export default {
    name: 'client-timeline',
    components: {
        ReadOnly,
        FileInfo,
        Editor,
        InputSelect,
        InputDatePicker,
        TimelineFilter
    },
    props: ['setup', 'demand'],
    data: function () {
        return {
            loading: false,
            fileLoading: false,
            previewFiles: [],
            filters: [],
            skip: 0,
            interaction: {
                count: 0,
                results: []
            },
            activity: {
                dialog: false,
                valid: false,
                item: {
                    demand: undefined,
                    annotation: undefined,
                    files: [],
                    category: 1,
                    isPrivate: false,
                    correctDate: ''
                },
                datePickers: {
                    correctDate: {
                        label: this.$t('common.timeline.correctDate.label'),
                        rule: [
                            v => !!v || this.$t('common.timeline.correctDate.rule')
                        ],
                        required: true,
                        readonly: false,
                        filled: true,
                        dateFormatted: undefined
                    }
                }
            },
            category: {
                label: this.$t('common.timeline.category.label'),
                text: 'label',
                value: 'value',
                icon: 'comment',
                required: true,
                readonly: false,
                filled: true,
                multiple: false,
                rule: [
                    v => !!v || this.$t('common.timeline.category.rule')
                ],
                items: [
                    { value: 1, label: this.$t('common.timeline.category.items.noteFile') },
                    { value: 2, label: this.$t('common.timeline.category.items.meeting') },
                    { value: 3, label: this.$t('common.timeline.category.items.phone') },
                    { value: 4, label: this.$t('common.timeline.category.items.email') },
                    { value: 6, label: this.$t('common.timeline.category.items.charge') }
                ],
                selectedItems: 1
            }
        }
    },
    watch: {
        'demand.interaction': {
            handler: function (val, oldVal) {
                this.interaction.results = this.demand.interaction
                this.$forceUpdate()
            },
            deep: true
        },
        'setup.timeline.loadData': {
            handler: function (val, oldVal) {
                if (oldVal !== val && val) {
                    this.loadData()
                }
            },
            deep: true
        }
    },
    mounted () {
        if (this.setup.timeline.loadData !== false) {
            this.loadData()
        }
    },
    computed: {
        isEmployee: function () {
            return this.$helpers.checkIsEmployee()
        },
        hasMoreInteractions () {
            if (this.interaction === undefined || this.interaction.results.length === 0) return
            return (this.interaction.results.length < this.interaction.count)
        }
    },
    methods: {
        loadData (filters = null, refresh = null) {
            this.loading = true
            window.getApp.disableClientNavigation = true
            this.filters = (filters) || []

            if (refresh) {
                this.skip = 0
                this.interaction.count = 0
                this.interaction.results = []
            }

            let endpoint = API.EndPoints.getInteractions
            let params = {
                filters: this.filters,
                className: this.setup.timeline.className,
                objectId: (this.setup.timeline.className !== 'Demand') ? this.$route.params.id : this.demand.objectId,
                skip: this.skip
            }

            new API(endpoint, params).request().then(response => {
                this.skip += response.data.result.results.length
                this.interaction.count = response.data.result.count
                this.interaction.results = [...this.interaction.results, ...response.data.result.results]
                this.loading = false
                this.setup.timeline.loadData = false
                window.getApp.disableClientNavigation = false
            }, (e) => {
                this.$helpers.requestError(e)
            })
        },
        async addFiles () {
            const file = event.target.files[0]
            const tempFile = file.slice(0, file.size)
            const newFile = new File([tempFile], file.name.replace(/[^a-zA-Z._0-9]/g, ''))
            this.previewFiles.push(newFile)
        },
        async uploadFile (file) {
            for (let index = 0; index < this.previewFiles.length; index++) {
                let endpoint = API.EndPoints.upload
                endpoint.filename = this.$helpers.normalizeFilename(this.previewFiles[index].name)

                await new API(endpoint, this.previewFiles[index]).request()
                    .then(response => {
                        const file = response.data
                        file.__type = 'File'
                        this.activity.item.files.push(file)
                    })
                    .catch(() => { alert(this.$t('common.timeline.category.alerts.saveError')) })
            }
        },
        async addActivity () {
            if (this.previewFiles.length > 0) {
                await this.uploadFile()
            }
            const currentUserCompany = JSON.parse(localStorage.getItem('currentUserCompany'))
            let items = this.activity.item

            if ((!items.annotation || items.annotation === '<p></p>') && items.files.length === 0) {
                alert(this.$t('common.timeline.category.alerts.addCommentOrFile'))
                return
            }

            items.category = this.category.selectedItems
            items.clientId = this.$route.params.id
            items.correctDate = { '__type': 'Date', 'iso': new Date(this.activity.datePickers.correctDate.dateFormatted.replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$3-$2-$1')).toISOString() }
            items.isEmployee = this.isEmployee
            items.profileId = currentUserCompany.objectId
            items.organizationId = currentUserCompany.client.objectId

            let endpoint = API.EndPoints.createInteraction
            new API(endpoint, items).request().then(response => {
                this.loadData(this.filters, true)
                this.clearActivity()
            }, (e) => {
                this.$helpers.requestError(e)
                this.clearActivity()
            })
        },
        clearFile (value) {
            if (!value) {
                this.loadData(this.filters, true)
            } else {
                this.filesPreview = this.filesPreview.filter((f) => f.file.name !== value)
            }
        },
        deleteActivity (index, activity) {
            if (confirm(this.$t('common.timeline.category.alerts.deleteConfirm'))) {
                window.getApp.refreshDemands = true

                if (this.setup.fromDemand) {
                    (!this.setup.isEdit) ? this.removeActivity(index) : this.removeActivityRest(activity)
                } else {
                    this.removeActivityRest(activity)
                }
            }
        },
        async removeActivityRest (interaction) {
            let endpoint = API.EndPoints.deleteInteraction
            endpoint.id = interaction.note.objectId
            let msg = ''
            await new API(endpoint).request()
                .then(
                    (response) => {
                        msg = this.$t('common.timeline.category.alerts.deleteSuccess')
                    },
                    (response) => { msg = (response.status !== 0) ? response.data.error : '' }
                )
            if (msg !== '') alert(msg)
            if (this.$refs.formActivity !== undefined) {
                this.clearActivity()
            }
            this.loadData(this.filters, true)
        },
        removeActivity (index) {
            this.activity.loading = true
            this.demand.interaction.splice(index, 1)
            this.activity.loading = false
            this.clearActivity()
        },
        clearActivity () {
            this.activity.dialog = false
            this.$refs.formActivity.reset()
            this.activity.valid = false
            this.activity.item.annotation = undefined
            this.activity.item.files = []
            this.previewFiles = []
            this.activity.item.isPrivate = false
            this.activity.loading = false
        }
    }
}
</script>
